import { type CompanyFormat, formatMoney, TextButton } from "@majitap/ui";
import { Badge } from "@majitap/ui/arc/badge/badge";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Fragment, useState } from "react";
import { RouterLink } from "../../router-link";
import { cellOf, type Zone, type ZoneSheet } from "../../zones";
import { CellPrice, ChargesList, CostsList, ZonePriceList } from "./prices";
import styles from "./zones.module.css";

type Money = Pick<CompanyFormat, "currency">;

/**
 * The zones as a list (P8-40, `Console / Service zones / List view`): each with its places and
 * what the chosen item costs there. A row opens to every item's price and the chosen one's
 * charges, costs and margin.
 */
export function ZonesTable({
  zones,
  sheet,
  itemId,
  format,
  onShowOnMap,
}: {
  zones: Zone[];
  /** null: the staff member can't see prices (orders.read) */
  sheet: ZoneSheet | null;
  itemId: string | undefined;
  format: Money;
  onShowOnMap: (zoneId: string) => void;
}) {
  const [open, setOpen] = useState<string | null>(null);
  const priced = sheet !== null;
  const columns = priced ? 7 : 3;
  const money = (v: string | null | undefined) => (v ? formatMoney(v, format) : "—");
  const itemName = sheet?.items.find((i) => i.id === itemId)?.name ?? "the chosen item";
  return (
    <div className={styles.tableWrap}>
      <table className={styles.table}>
        <caption className={styles.srOnly}>Service zones{priced ? " and their prices" : ""}</caption>
        <thead>
          <tr>
            <th scope="col">Zone</th>
            <th scope="col">Status</th>
            <th scope="col" className={styles.num}>
              Places
            </th>
            {priced ? (
              <>
                <th scope="col">Price</th>
                <th scope="col" className={styles.num}>
                  Customer pays
                </th>
                <th scope="col" className={styles.num}>
                  Costs
                </th>
                <th scope="col" className={styles.num}>
                  Margin
                </th>
              </>
            ) : null}
          </tr>
        </thead>
        <tbody>
          {zones.length === 0 ? (
            <tr>
              <td colSpan={columns} className={styles.mutedInline}>
                No zones yet. Draw the first with New zone.
              </td>
            </tr>
          ) : null}
          {zones.map((z) => {
            const cell = cellOf(sheet ?? undefined, z.id, itemId);
            const isOpen = open === z.id;
            return (
              <Fragment key={z.id}>
                <tr className={isOpen ? styles.rowOpen : z.status === "inactive" ? styles.rowOff : undefined}>
                  <td>
                    <button
                      type="button"
                      className={styles.expand}
                      aria-expanded={isOpen}
                      onClick={() => setOpen(isOpen ? null : z.id)}
                    >
                      {isOpen ? <ChevronDown size={16} aria-hidden /> : <ChevronRight size={16} aria-hidden />}
                      <span className={styles.stack}>
                        <span className={styles.zoneName}>{z.name}</span>
                        <span className={styles.mutedInline}>{z.area_km2 ? `${z.area_km2} km²` : "No area"}</span>
                      </span>
                    </button>
                  </td>
                  <td>
                    <Badge tone={z.status === "active" ? "success" : "neutral"}>
                      {z.status === "active" ? "Active" : "Off"}
                    </Badge>
                  </td>
                  <td className={styles.num}>{z.location_count}</td>
                  {priced ? (
                    <>
                      <td className={styles.priceCell}>
                        <CellPrice cell={cell} zoneName={z.name} format={format} />
                      </td>
                      <td className={styles.num}>{money(cell?.receivable_total)}</td>
                      <td className={styles.num}>{money(cell?.payable_total)}</td>
                      <td className={styles.num}>{money(cell?.margin)}</td>
                    </>
                  ) : null}
                </tr>
                {isOpen ? (
                  <tr className={styles.rowDetail}>
                    <td colSpan={columns}>
                      <div className={styles.detail}>
                        {sheet ? (
                          <div className={styles.cards}>
                            <section className={styles.priceCard} aria-label="Every item">
                              <h3 className={styles.cardTitle}>Prices · customer pays</h3>
                              <ZonePriceList sheet={sheet} zoneId={z.id} zoneName={z.name} format={format} inline />
                            </section>
                            {cell && !cell.missing && cell.receivable_total && cell.margin ? (
                              <>
                                <section className={styles.priceCard} aria-label="Charges">
                                  <h3 className={styles.cardTitle}>{itemName} · one delivery</h3>
                                  <ChargesList
                                    lines={cell.lines}
                                    receivable={cell.receivable_total}
                                    zoneName={z.name}
                                    format={format}
                                  />
                                </section>
                                <section className={styles.priceCard} aria-label="Costs">
                                  <h3 className={styles.cardTitle}>Costs · MajiTap pays</h3>
                                  <CostsList
                                    lines={cell.lines}
                                    margin={cell.margin}
                                    zoneName={z.name}
                                    format={format}
                                  />
                                </section>
                              </>
                            ) : null}
                          </div>
                        ) : (
                          <p className={styles.muted}>Prices need the orders.read permission.</p>
                        )}
                        <div className={styles.actions}>
                          <TextButton onClick={() => onShowOnMap(z.id)}>Show on the map</TextButton>
                          <RouterLink href={`/pricing?geofence_id=${z.id}`} className={styles.link}>
                            Its rules in Pricing
                          </RouterLink>
                        </div>
                      </div>
                    </td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </table>
      {priced ? (
        <div className={styles.tableFoot}>
          <span>
            {zones.length === 1 ? "1 zone" : `${zones.length} zones`} · prices in force today, for {itemName}
          </span>
          <span>
            <span className={styles.sourceOwn}>Zone name</span>: the zone sets the price · otherwise the any-zone price
            applies
          </span>
        </div>
      ) : null}
    </div>
  );
}
